import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function EstoqueSaida() {
  const navigate = useNavigate();

  // Form state – "item" will be the selected item's id.
  const [formData, setFormData] = useState({
    item: "",
    quantidade: "",
    observacao: "",
    responsavel: "",
    telefone: "",
    tipo_item: "",
    destino: "",
  });
  const [mensagem, setMensagem] = useState(null);
  const [erro, setErro] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Lista para o dropdown com itens únicos (agrupados por descrição e tipo_item)
  const [availableItems, setAvailableItems] = useState([]);
  // Lista com todos os itens disponíveis (sem remoção de duplicatas)
  const [allAvailableItems, setAllAvailableItems] = useState([]);

  // Helper: format phone number
  const formatTelefone = (value) => {
    value = value.replace(/\D/g, "");
    value = value.slice(0, 11);
    if (value.length <= 10) {
      return value
        .replace(/^(\d{2})(\d)/, "($1) $2")
        .replace(/(\d{4})(\d)/, "$1-$2");
    } else {
      return value
        .replace(/^(\d{2})(\d)/, "($1) $2")
        .replace(/(\d{5})(\d)/, "$1-$2");
    }
  };

  // Atualiza formData conforme mudança dos inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    if (name === "telefone") {
      formattedValue = formatTelefone(value);
    } else if (name === "destino" || name === "observacao" || name === "responsavel") {
      formattedValue = value.toUpperCase();
    }
    setFormData({ ...formData, [name]: formattedValue });
  };

  // Quando o select de item muda, atualiza o item selecionado e seu tipo_item
  const handleItemChange = (e) => {
    const selectedId = e.target.value;
    const selectedItem = availableItems.find(
      (item) => String(item.id) === selectedId
    );
    setFormData({
      ...formData,
      item: selectedId,
      tipo_item: selectedItem ? selectedItem.tipo_item : "",
    });
  };

  // Buscar os itens disponíveis ao montar o componente
  useEffect(() => {
    async function fetchAvailableItems() {
      try {
        const response = await fetch(`${API_BASE_URL}estoque/`);
        if (!response.ok) {
          throw new Error("Erro ao buscar os itens disponíveis");
        }
        const items = await response.json();
        // Extrai os itens e filtra aqueles com quantidade_atual > 0
        const allItems = items
          .filter((mov) => parseFloat(mov.item.quantidade_atual) > 0)
          .map((mov) => mov.item);
        setAllAvailableItems(allItems);
        // Remove duplicatas para o dropdown – considera a descrição e tipo_item
        const uniqueItems = allItems.reduce((acc, curr) => {
          if (
            !acc.find(
              (itm) =>
                itm.descricao === curr.descricao &&
                itm.tipo_item === curr.tipo_item
            )
          ) {
            acc.push(curr);
          }
          return acc;
        }, []);
        setAvailableItems(uniqueItems);
      } catch (error) {
        console.error("Erro ao carregar itens:", error);
      }
    }
    fetchAvailableItems();
  }, []);

  // Handler para o envio do formulário de saída
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErro(null);
    setMensagem(null);
  
    try {
      if (!formData.item) {
        throw new Error("Selecione um item disponível para saída.");
      }
      // Obtém o item selecionado no dropdown (único, agrupado por descrição)
      const selectedItem = availableItems.find(
        (item) => String(item.id) === formData.item
      );
      if (!selectedItem) {
        throw new Error("Item não encontrado nos registros.");
      }
      // Filtra todos os itens disponíveis com a mesma descrição (podem ter origens diferentes)
      const filteredItems = allAvailableItems.filter(
        (item) =>
          item.descricao === selectedItem.descricao &&
          parseFloat(item.quantidade_atual) > 0
      );
  
      // Agrega a quantidade total disponível entre os registros
      const aggregatedQuantity = filteredItems.reduce(
        (sum, item) => sum + parseFloat(item.quantidade_atual),
        0
      );
      const requestedQuantity = parseFloat(formData.quantidade);
      if (requestedQuantity > aggregatedQuantity) {
        throw new Error(
          "Quantidade solicitada para saída excede a quantidade disponível."
        );
      }
  
      // Distribui a quantidade solicitada entre os registros disponíveis
        let remaining = requestedQuantity;
        const payloads = [];
        for (const item of filteredItems) {
          const available = parseFloat(item.quantidade_atual);
          if (available > 0 && remaining > 0) {
            const quantityToUse = Math.min(available, remaining);
            remaining -= quantityToUse;
            // Aqui garantimos que cada payload receba a origem do item
            const payload = {
              item_id: item.id,
              quantidade: quantityToUse,
              tipo: "Saida",
              destino: formData.destino,
              observacao: formData.observacao,
              responsavel: formData.responsavel,
              telefone: formData.telefone,
              origem: item.origem // se for obrigatório
            };
            payloads.push(payload);
          }
        }
      // Se por algum motivo ainda restar quantidade, aborta
      if (remaining > 0) {
        throw new Error(
          "Quantidade solicitada para saída excede a quantidade disponível."
        );
      }
  
      // Envia a requisição única para o endpoint bulk
      const response = await fetch(`${API_BASE_URL}estoque/movimentos/bulk/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payloads),
      });
      if (!response.ok) {
        // Tenta parsear JSON, mas cai para texto bruto se não for JSON
        let errorText;
        const contentType = response.headers.get("Content-Type") || "";
        if (contentType.includes("application/json")) {
          const errorData = await response.json();
          errorText = Object.values(errorData).flat().join(" ");
        } else {
          errorText = await response.text();
        }
        throw new Error(errorText || "Erro ao cadastrar saída.");
      }
  
      setMensagem("Saída cadastrada com sucesso!");
      // Reseta o formulário
      setFormData({
        item: "",
        quantidade: "",
        observacao: "",
        responsavel: "",
        telefone: "",
        tipo_item: "",
        destino: "",
      });
      setTimeout(() => {
        setMensagem("");
        navigate("/estoque");
      }, 3000);
    } catch (error) {
      console.error("Erro ao enviar para API:", error);
      setErro(error.message || "Ocorreu um erro ao enviar o formulário.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-clear messages after 5 seconds
  useEffect(() => {
    if (mensagem || erro) {
      const timer = setTimeout(() => {
        setMensagem("");
        setErro("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [mensagem, erro]);

  return (
    <AdminLayout>
      <div className="container mt-4">
        <div className="col-12">
          <form className="card" onSubmit={handleSubmit}>
            <div className="card-body">
              <h3 className="card-title">Registrar Saída</h3>
              {mensagem && (
                <div className="alert alert-success">{mensagem}</div>
              )}
              {erro && <div className="alert alert-danger">{erro}</div>}
              <div className="row row-cards">
                <h4 className="mt-4">Informações do Item</h4>
                {/* Select de Item – listando os itens disponíveis */}
                <div className="col-md-8">
                  <div className="mb-3">
                    <label className="form-label">Item</label>
                    <select
                      className="form-select"
                      name="item"
                      onChange={handleItemChange}
                      value={formData.item}
                      required
                    >
                      <option value="">Selecione um item</option>
                      {availableItems.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.descricao.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Quantidade para saída */}
                <div className="col-md-2">
                  <div className="mb-3">
                    <label className="form-label">Quantidade</label>
                    <input
                      type="number"
                      className="form-control"
                      name="quantidade"
                      onChange={handleChange}
                      value={formData.quantidade}
                      placeholder="0"
                      required
                    />
                  </div>
                </div>
                {/* Tipo do item (auto preenchido com base no item selecionado) */}
                <div className="col-md-2">
                  <div className="mb-3">
                    <label className="form-label">Tipo</label>
                    <input
                      type="text"
                      className="form-control"
                      name="tipo_item"
                      style={{ textTransform: "uppercase" }}
                      value={formData.tipo_item}
                      disabled
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Destino</label>
                    <input
                      type="text"
                      className="form-control"
                      name="destino"
                      onChange={handleChange}
                      value={formData.destino}
                      placeholder="Informe o destino"
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Observação</label>
                    <input
                      type="text"
                      className="form-control"
                      name="observacao"
                      onChange={handleChange}
                      value={formData.observacao}
                      placeholder="Observação (opcional)"
                    />
                  </div>
                </div>
                <h4 className="mt-4">
                  Informações do Responsável pela Saída
                </h4>
                {/* Nome do Responsável */}
                <div className="col-md-8">
                  <div className="mb-3">
                    <label className="form-label">Nome</label>
                    <input
                      type="text"
                      className="form-control"
                      name="responsavel"
                      onChange={handleChange}
                      value={formData.responsavel}
                      placeholder="Nome do responsável"
                      required
                    />
                  </div>
                </div>
                {/* Telefone do Responsável */}
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Telefone</label>
                    <input
                      type="text"
                      className="form-control"
                      name="telefone"
                      onChange={handleChange}
                      value={formData.telefone}
                      placeholder="(00) 00000-0000"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card-footer text-end">
              <button
                type="button"
                className="btn btn-secondary me-2"
                onClick={() =>
                  setFormData({
                    item: "",
                    quantidade: "",
                    observacao: "",
                    responsavel: "",
                    telefone: "",
                    tipo_item: "",
                    destino: "",
                  })
                }
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}

export default EstoqueSaida;